import React, { createContext, useEffect, useState } from 'react'

// Define the context type
interface TimezoneContextType {
  timezone: string
  setTimezone: (timezone: string) => void
}
type StaticMethods = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [key: string]: Function
}

// Create the context with initial undefined value
export const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined)

// Define props type for TimezoneProvider
interface TimezoneProviderProps {
  children: React.ReactNode
  defaultTimezone?: string
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({
  children,
  defaultTimezone = 'UTC'
}) => {
  const [timezone, setTimezone] = useState<string>(defaultTimezone)

  // Store the original Date constructor in a ref to maintain it across renders
  const originalDate = React.useRef<DateConstructor>(Date)

  // Function to convert date to target timezone
  const convertToTimezone = (date: Date, targetTimezone: string): Date => {
    try {
      const dateString = date.toLocaleString('en-US', {
        timeZone: targetTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      const [hours, minutes] = dateString.split(':').map(Number)
      date.setHours(hours)
      date.setMinutes(minutes)
      return new originalDate.current(date)
    } catch (e) {
      console.error('Timezone conversion failed:', e)
      return date
    }
  }

  // Override the global Date constructor
  useEffect(() => {
    // Custom Date class that auto-converts to target timezone
    class TimezoneDate extends originalDate.current {
      constructor(...args: ConstructorParameters<DateConstructor>) {
        // Handle no args (current time) or timestamp/string args
        super(...args)

        // Only convert if we have a valid date and timezone is not UTC
        if (!isNaN(this.getTime()) && timezone !== 'UTC') {
          const adjustedDate = convertToTimezone(this, timezone)
          super.setTime(adjustedDate.getTime())
        }
      }
    }

    // Use window instead of global for browser environments
    const globalObj = typeof window !== 'undefined' ? window : global
    globalObj.Date = TimezoneDate as unknown as DateConstructor

    // Copy static methods
    Object.getOwnPropertyNames(originalDate.current).forEach((prop) => {
      const currentMethod = originalDate.current as unknown as StaticMethods
      if (!(prop in TimezoneDate)) {
        ;(TimezoneDate as unknown as StaticMethods)[prop] = currentMethod[prop]
      }
    })

    // Restore original Date when component unmounts
    return () => {
      globalObj.Date = originalDate.current
    }
  }, [timezone])

  return (
    <TimezoneContext.Provider value={{ timezone, setTimezone }}>
      {children}
    </TimezoneContext.Provider>
  )
}
